import type { TFunction } from "i18next";
import { type DateTime, Settings } from "luxon";
import { type HolidayJobWindow, HolidayStatus } from "../holidayApi";
import type { ScheduleHoliday } from "./scheduleHolidays";

/** "Thu 7:00 AM" */
const WINDOW_TIME_FORMAT = "ccc h:mm a";
/** Unicode first-strong isolate and its closing mark: a time keeps its own direction inside a right-to-left line. */
const ISOLATE_START = "⁨";
const ISOLATE_END = "⁩";

/** The holiday's hours have started, so Apply can't change the schedule any more (the server's `canChangeGroup`). */
const hasStarted = ({ holiday }: ScheduleHoliday): boolean => !holiday.canChangeGroup;

/** The ribbon's hover text: "Christmas · Group A", "… · not applied", "Christmas · No group yet", or just "Christmas" once its applied group was removed. */
export const scheduleHolidayTooltip = (t: TFunction, { holiday, group, applied }: ScheduleHoliday): string => {
	if (group === null) return applied ? holiday.name : t("holidays.schedule.ribbon.tooltipNoGroup", { name: holiday.name });
	return t(applied ? "holidays.schedule.ribbon.tooltip" : "holidays.schedule.ribbon.tooltipNotApplied", { name: holiday.name, group: group.label });
};

/** "Christmas holiday schedule · Group A works", "… · No group chosen yet", or without the group once its applied group was removed. */
export const scheduleHolidayBannerTitle = (t: TFunction, { holiday, group, applied }: ScheduleHoliday): string => {
	if (group !== null) return t("holidays.schedule.banner.title", { name: holiday.name, group: group.label });
	return t(applied ? "holidays.schedule.banner.titleAppliedNoGroup" : "holidays.schedule.banner.titleNoGroup", { name: holiday.name });
};

/** Whether the holiday's shifts are on the schedule, in the past tense once its hours started. */
export const scheduleHolidayStatusLine = (t: TFunction, scheduleHoliday: ScheduleHoliday): string => {
	if (scheduleHoliday.applied) return t("holidays.schedule.banner.applied");
	return t(hasStarted(scheduleHoliday) ? "holidays.schedule.banner.notAppliedStarted" : "holidays.schedule.banner.notApplied");
};

/**
 * Only once the chosen group changed after Apply: the schedule still shows the applied group until the next Apply,
 * or, once the hours started, kept it for good.
 */
export const scheduleHolidayChangedLine = (t: TFunction, scheduleHoliday: ScheduleHoliday): string | null => {
	const { holiday, group, selectedGroup } = scheduleHoliday;
	if (holiday.status !== HolidayStatus.Changed) return null;
	if (hasStarted(scheduleHoliday)) return group === null ? null : t("holidays.schedule.banner.changedStarted", { group: group.label });
	return selectedGroup === null ? t("holidays.schedule.banner.changedNoGroup") : t("holidays.schedule.banner.changed", { group: selectedGroup.label });
};

export type HolidayJobLine = { jobIds: string[]; text: string };

const windowTime = (time: DateTime, timezone: string): string =>
	// A loaded holiday outlives a language switch, and a DateTime keeps the locale it was created with.
	`${ISOLATE_START}${time.setZone(timezone).setLocale(Settings.defaultLocale).toFormat(WINDOW_TIME_FORMAT)}${ISOLATE_END}`;

/** One line per set of hours ("Nurse, Aide: Thu 7:00 AM → Fri 7:00 AM"), in job order; one "All jobs" line when every job matches. */
export const holidayJobLines = (t: TFunction, windows: readonly HolidayJobWindow[], jobName: (jobId: string) => string, timezone: string): HolidayJobLine[] => {
	const byHours = new Map<string, { window: HolidayJobWindow; jobIds: string[] }>();
	for (const window of windows) {
		const hoursKey = `${window.start.toMillis()}-${window.end.toMillis()}`;
		const line = byHours.get(hoursKey);
		if (line === undefined) byHours.set(hoursKey, { window, jobIds: [window.jobId] });
		else line.jobIds.push(window.jobId);
	}
	const isAllJobs = byHours.size === 1 && windows.length > 1;
	return [...byHours.values()].map(({ window, jobIds }) => {
		const hours = { start: windowTime(window.start, timezone), end: windowTime(window.end, timezone) };
		return {
			jobIds,
			text: isAllJobs
				? t("holidays.schedule.banner.allJobsLine", hours)
				: t("holidays.schedule.banner.jobLine", { jobs: jobIds.map(jobName).join(", "), ...hours }),
		};
	});
};
